import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploadService } from '../../services/file-upload.service';
import { ToastService } from '../../services/toast.service';

@Component({
	selector: 'app-file-uploader',
	templateUrl: './file-uploader.component.html',
	styleUrls: ['./file-uploader.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FileUploaderComponent),
			multi: true
		}
	]
})
export class FileUploaderComponent implements ControlValueAccessor {
	@Input() accept: string = '.svg,image/svg+xml';
	@Input() uploadType: 'icon' | 'image' | 'json' | 'csv' | 'document' = 'icon';
	@Input() label: string = 'Upload File';
	@Input() uploadButtonText: string = 'Upload';
	@Input() changeButtonText: string = 'Change';
	@Input() uploadingText: string = 'Uploading...';
	@Input() maxFileSizeMB: number = 5;
	@Input() validateFileType: boolean = true;
	@Input() showPreview: boolean = true;
	@Input() autoUpload: boolean = true;

	@Output() uploadSuccess = new EventEmitter<string>();
	@Output() uploadError = new EventEmitter<any>();
	@Output() fileSelected = new EventEmitter<File>();

	fileUrl: string | null = null;
	selectedFile: File | null = null;
	isUploading: boolean = false;
	inputId: string = `fileUploadInput_${Math.random().toString(36).substr(2, 9)}`;
	private onChange: (value: string | null) => void = () => { };
	private onTouched: () => void = () => { };

	constructor(
		private fileUploadService: FileUploadService,
		private toastService: ToastService
	) { }

	writeValue(value: string | null): void {
		this.fileUrl = value;
	}

	registerOnChange(fn: (value: string | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const file = input.files[0];

			if (file.size > this.maxFileSizeMB * 1024 * 1024) {
				this.toastService.error(`File size exceeds ${this.maxFileSizeMB}MB limit`);
				return;
			}

			if (this.validateFileType) {
				const acceptedTypes = this.accept.split(',').map(t => t.trim());
				const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
				const mimeType = file.type;

				const isValid = acceptedTypes.some(type => {
					if (type.startsWith('.')) {
						return fileExtension === type.toLowerCase();
					}
					return mimeType === type;
				});

				if (!isValid) {
					this.toastService.error(`Please select a valid file type: ${this.accept}`);
					return;
				}
			}

			this.selectedFile = file;
			if (!this.autoUpload) {
				this.onChange(file as any);
				this.onTouched();
				this.fileSelected.emit(file);
				return;
			}

			this.isUploading = true;
			this.uploadFile(file);
		}
	}

	public uploadSelectedFile(): void {
		if (this.selectedFile && !this.autoUpload) {
			this.isUploading = true;
			this.uploadFile(this.selectedFile);
		}
	}

	private uploadFile(file: File): void {
		let uploadObservable;

		switch (this.uploadType) {
			case 'icon':
				uploadObservable = this.fileUploadService.uploadIcon(file);
				break;
			case 'image':
				uploadObservable = this.fileUploadService.uploadImage(file);
				break;
			case 'json':
				uploadObservable = this.fileUploadService.uploadJson(file);
				break;
			case 'csv':
				uploadObservable = this.fileUploadService.uploadCsv(file);
				break;
			case 'document':
				uploadObservable = this.fileUploadService.uploadDocument(file);
				break;
			default:
				uploadObservable = this.fileUploadService.uploadIcon(file);
		}

		uploadObservable.subscribe(
			(response) => {
				this.isUploading = false;
				this.fileUrl = response.path;
				this.onChange(response.path);
				this.onTouched();
				this.uploadSuccess.emit(response.path);
				this.toastService.success('File uploaded successfully', 'File Upload', 6000);
			},
			(error) => {
				this.isUploading = false;
				this.uploadError.emit(error);
				this.toastService.error('Failed to upload file: ' + (error.error?.message || 'Unknown error'));
			}
		);
	}

	clearFile(): void {
		this.fileUrl = null;
		this.selectedFile = null;
		this.onChange(null);
		this.onTouched();
	}

	get selectedFileName(): string {
		return this.selectedFile?.name || '';
	}
}
